import React, { useState, useCallback } from 'react'
import { useAuth, useUser } from '@clerk/clerk-react'
import { Plus, Edit, Trash2, PlusCircle, MinusCircle, Sparkles, ChevronDown, X, Image as ImageIcon, Upload, Type, BrainCircuit, AlertCircle as LucideAlertCircle } from 'lucide-react'
import { useToast } from "../../hooks/use-toast.jsx"
import { Button } from "../ui/button.jsx"
import { Input } from "../ui/input.jsx"
import { Label } from "../ui/label.jsx"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "../ui/dialog.jsx"
import { Card, CardContent, CardFooter } from "../ui/card.jsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "../ui/select.jsx"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion.jsx"
import { RadioGroup, RadioGroupItem } from "../ui/radio-group.jsx"
import MultiSelectCreatable from "../ui/MultiSelectCreatable.js"
import { cn } from "../../lib/utils.js"
import { storage } from '../../lib/firebase.js';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid'
import { CircularProgress, Typography, Box } from '@mui/material'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { Alert, AlertDescription, AlertTitle } from "../ui/alert.jsx"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog.jsx"
import { User } from 'lucide-react'

// --- Greatly Expanded and Categorized Options --- 

const traitCategories = [
  { label: 'Personality (Positive)', options: ['Kind', 'Brave', 'Curious', 'Funny', 'Adventurous', 'Creative', 'Logical', 'Patient', 'Determined', 'Optimistic', 'Generous', 'Loyal', 'Honest', 'Resourceful', 'Adaptable', 'Confident', 'Empathetic', 'Charming', 'Wise', 'Humble'] },
  { label: 'Personality (Neutral/Situational)', options: ['Shy', 'Quiet', 'Serious', 'Reserved', 'Skeptical', 'Dreamy', 'Impulsive', 'Competitive', 'Practical', 'Idealistic', 'Cautious', 'Spontaneous', 'Methodical', 'Whimsical'] },
  { label: 'Personality (Negative)', options: ['Grumpy', 'Pessimistic', 'Stubborn', 'Arrogant', 'Impatient', 'Lazy', 'Greedy', 'Jealous', 'Vain', 'Anxious', 'Bossy', 'Clumsy', 'Forgetful', 'Naive'] },
  { label: 'Skills/Aptitudes', options: ['Intelligent', 'Athletic', 'Artistic', 'Musical', 'Tech-savvy', 'Good Listener', 'Problem Solver', 'Organized', 'Persuasive', 'Observant'] },
];

const abilityCategories = [
  { label: 'Physical', options: ['Super Speed', 'Super Strength', 'Flight', 'Enhanced Agility', 'Enhanced Senses', 'Wall-Crawling', 'Regeneration', 'Invisibility', 'Shape-shifting'] },
  { label: 'Mental/Psychic', options: ['Telekinesis', 'Telepathy', 'Mind Control', 'Precognition', 'Illusion Casting', 'Memory Manipulation', 'Rapid Learning'] },
  { label: 'Elemental/Nature', options: ['Control Water', 'Control Fire', 'Control Earth', 'Control Air', 'Control Weather', 'Talking to Animals', 'Plant Manipulation'] },
  { label: 'Magic/Energy', options: ['Spell Casting', 'Energy Blasts', 'Healing', 'Force Fields', 'Teleportation', 'Summoning', 'Alchemy'] },
  { label: 'Technology/Gadgets', options: ['Gadgeteer', 'Hacking', 'Powered Armor', 'Robotics Expert', 'Weapon Master'] },
  { label: 'Other/Unique', options: ['Luck Manipulation', 'Reality Warping (minor)', 'Fourth-Wall Awareness', 'Cartoon Physics', 'Musical Ability (Magical)', 'Dream Walking'] },
];

const likeCategories = [
  { label: 'Hobbies/Activities', options: ['Reading', 'Playing Outside', 'Drawing', 'Singing', 'Building', 'Puzzles', 'Games (Video)', 'Games (Board)', 'Sports', 'Dancing', 'Cooking/Baking', 'Gardening', 'Crafting', 'Collecting', 'Stargazing'] },
  { label: 'Social', options: ['Helping Others', 'Making Friends', 'Family Time', 'Parties', 'Teamwork', 'Sharing', 'Talking', 'Listening to Stories'] },
  { label: 'Sensory/Food', options: ['Music', 'Sweet Treats', 'Sunny Days', 'Rainy Days', 'Soft Blankets', 'Hugs', 'Nature Sounds', 'Favorite Foods', 'Smell of Cookies'] },
  { label: 'Learning/Thinking', options: ['Learning New Things', 'Solving Problems', 'Exploring', 'Asking Questions', 'Daydreaming', 'Science Experiments'] },
  { label: 'Animals/Nature', options: ['Animals (General)', 'Puppies', 'Kittens', 'Forests', 'Oceans', 'Flowers', 'Bugs (Friendly)'] },
];

const dislikeCategories = [
  { label: 'Sensory/Food', options: ['Loud Noises', 'Bright Lights', 'Bad Smells', 'Being Cold', 'Being Hot', 'Scratchy Clothes', 'Vegetables', 'Spicy Food', 'Bitter Flavors'] },
  { label: 'Situations', options: ['Being Alone', 'Waking Up Early', 'Bedtime', 'Chores', 'Waiting', 'Being Rushed', 'Doctor Visits', 'Getting Lost', 'Arguments', 'Tests'] },
  { label: 'Social', options: ['Bullies', 'Being Teased', 'Sharing (Sometimes)', 'Being Ignored', 'Large Crowds', 'Being the Center of Attention'] },
  { label: 'Phobias/Creatures', options: ['Spiders', 'The Dark', 'Monsters', 'Ghosts', 'Snakes', 'Insects (Scary)'] },
  { label: 'Abstract', options: ['Messiness', 'Unfairness', 'Rules (Sometimes)', 'Change', 'Surprises (Bad)'] },
];

const fearCategories = [
  { label: 'Common Phobias', options: ['Spiders (Arachnophobia)', 'Heights (Acrophobia)', 'The Dark (Nyctophobia)', 'Snakes (Ophidiophobia)', 'Dogs (Cynophobia)', 'Enclosed Spaces (Claustrophobia)', 'Water (Aquaphobia)', 'Needles (Trypanophobia)', 'Thunder/Lightning (Astraphobia)', 'Clowns (Coulrophobia)'] },
  { label: 'Social/Emotional Fears', options: ['Being Alone (Monophobia)', 'Public Speaking (Glossophobia)', 'Failure (Atychiphobia)', 'Being Judged', 'Rejection', 'Making Mistakes', 'Losing Loved Ones', 'Change (Metathesiophobia)', 'Conflict', 'Embarrassment'] },
  { label: 'Abstract/Existential Fears', options: ['The Unknown', 'The Future', 'Monsters Under the Bed', 'Nightmares', 'Loud Noises (Ligyrophobia)', 'Getting Lost', 'Death (Thanatophobia)', 'Being Forgotten'] },
  { label: 'Situational Fears', options: ['Doctors/Dentists (Iatrophobia/Dentophobia)', 'Flying (Aerophobia)', 'Tests/Exams', 'Large Crowds (Agoraphobia/Enochlophobia)', 'Germs (Mysophobia)', 'Being Trapped'] }
];

const dreamCategories = [
  { label: 'Adventure/Exploration', options: ['Flying Freely', 'Exploring Space/Planets', 'Sailing the High Seas', 'Discovering Lost Civilizations', 'Time Traveling', 'Climbing the Tallest Mountain', 'Journey Through a Magical Forest', 'Deep Sea Diving', 'Archaeological Digs'] },
  { label: 'Skill/Achievement', options: ['Becoming a Renowned Hero', 'Mastering Ancient Magic', 'Becoming a Famous Musician/Artist', 'Winning the Grand Championship', 'Building an Invention that Changes the World', 'Solving an Unsolvable Riddle', 'Writing a Bestselling Book', 'Graduating with Honors'] },
  { label: 'Relationships/Community', options: ['Making Lifelong Friends', 'Finding True Love', 'Helping the Needy', 'Having a Big, Happy Family', 'Leading a Successful Team', 'Bringing Peace to Warring Factions', 'Creating a Welcoming Community', 'Being Understood'] },
  { label: 'Fantasy/Wonder', options: ['Talking to Animals Fluently', 'Having Versatile Superpowers', 'Living in a Magnificent Castle', 'Befriending Dragons/Unicorns', 'Finding Legendary Treasure', 'Having an Endless Supply of Favorite Things', 'Walking on Clouds', 'Visiting Dream Worlds'] },
  { label: 'Personal Growth', options: ['Overcoming Fears', 'Becoming Confident', 'Finding Inner Peace', 'Learning About Oneself', 'Achieving Enlightenment', 'Making a Difference'] },
];

// Define field configurations
const additionalFieldConfigs = {
  'Fears': { type: 'multiselect', categories: fearCategories, placeholder: "Select or add fears...", creatablePlaceholder: "Add new fear..." },
  'Dreams': { type: 'multiselect', categories: dreamCategories, placeholder: "Select or add dreams...", creatablePlaceholder: "Add new dream..." },
  'Backstory': { type: 'text', placeholder: "Enter backstory..." },
  'Appearance': { type: 'text', placeholder: "Describe appearance..." },
  'Role': { type: 'text', placeholder: "e.g., Protagonist, Friend, Mentor..." }
  // Add more field configs here if needed
};

// Base list of optional field names
const baseOptionalFieldNames = Object.keys(additionalFieldConfigs);

// Define some common initial options (can be expanded)
const initialTraitOptions = ["Kind", "Brave", "Curious", "Funny", "Shy", "Adventurous", "Creative", "Logical"];
const initialAbilityOptions = ["Super Speed", "Invisibility", "Telekinesis", "Healing", "Flight", "Talking to Animals"];
const initialLikeOptions = ["Reading", "Playing Outside", "Drawing", "Singing", "Building", "Helping Others"];
const initialDislikeOptions = ["Loud Noises", "Being Alone", "Spiders", "Vegetables", "Waking Up Early"];
const initialFearsOptions = ["The Dark", "Heights", "Spiders", "Being Alone", "Loud Noises", "Failure"];
const initialDreamsOptions = ["Flying", "Becoming a Hero", "Exploring", "Making Friends", "Learning Magic", "Building a Treehouse"];

// Update the relationshipTypes structure
const relationshipTypes = [
  {
    label: 'Family',
    options: ['Father', 'Mother', 'Son', 'Daughter', 'Brother', 'Sister', 'Husband', 'Wife', 'Partner', 'Grandparent', 'Grandchild', 'Cousin', 'Aunt', 'Uncle', 'Niece', 'Nephew', 'Pet']
  },
  {
    label: 'Friendship',
    options: ['Best Friend', 'Close Friend', 'Friend', 'Acquaintance']
  },
  {
    label: 'Romantic',
    options: ['Love Interest', 'Ex-partner']
  },
  {
    label: 'Mentorship/Guidance',
    options: ['Mentor', 'Mentee', 'Teacher', 'Student', 'Guardian']
  },
  {
    label: 'Companionship/Team',
    options: ['Sidekick', 'Ally', 'Teammate', 'Colleague', 'Neighbor']
  },
  {
    label: 'Conflict',
    options: ['Rival', 'Enemy', 'Nemesis', 'Bully']
  }
  // 'Other' category removed
];

// Add to newCharacter state:
// isAnimal: false, animalType: ''

// Add at the top of the component
const animalOptions = [
  'Dog', 'Cat', 'Bird', 'Fish', 'Hamster', 'Rabbit', 'Guinea Pig', 'Ferret', 'Turtle', 'Lizard', 'Snake', 'Horse', 'Frog', 'Mouse', 'Rat', 'Other'
];

// Helper function to convert Data URL to Blob
function dataURItoBlob(dataURI) {
  if (!dataURI || !dataURI.includes(',')) {
    console.error("Invalid data URI provided to dataURItoBlob:", dataURI ? dataURI.substring(0, 50) + '...' : 'null');
    return null; 
  }
  try {
    // Ensure correct base64 padding (might be needed for some strings)
    let base64 = dataURI.split(',')[1];
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    // Pad with '=' characters if necessary
    // base64 += '=='.slice(0, (4 - base64.length % 4) % 4); // Padding often handled by atob, but good to be aware of

    const byteString = atob(base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  } catch (error) {
    console.error("Error converting data URI to Blob:", error, dataURI.substring(0, 50) + '...');
    return null; 
  }
}

export default function Characters({ 
  userTier, 
  characters, 
  addCharacter, 
  updateCharacter, 
  deleteCharacter, 
  isDialogOpen,
  setIsDialogOpen
}) {
  const [editingCharacter, setEditingCharacter] = useState(null)
  const [newCharacter, setNewCharacter] = useState({
    id: '',
    name: '',
    age: '',
    gender: 'Female',
    avatar: null,
    traits: [],
    specialAbility: [],
    likes: [],
    dislikes: [],
    fears: [],
    dreams: [],
    additionalFields: {},
    relationships: [],
    seed: null,
    isAnimal: false,
    animalType: ''
  })
  const [avatarPrompt, setAvatarPrompt] = useState('');
  const [generatedAvatar, setGeneratedAvatar] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [relationshipSearchTerm, setRelationshipSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [avatarConfirmed, setAvatarConfirmed] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null);
  const { toast } = useToast();
  const { getToken } = useAuth();
  const { user: clerkUser } = useUser();
  const isAdmin = clerkUser?.publicMetadata?.isAdmin === true;

  const handleAddOrUpdateCharacter = async () => {
    if (!newCharacter.name || !newCharacter.age || !newCharacter.gender) {
      toast({
        title: "Missing Required Fields",
        description: "Please fill in all required fields (Name, Age, and Gender)",
        variant: "destructive",
      });
      return;
    }

    // --- Safeguard against saving base64 avatar ---
    if (newCharacter.avatar && newCharacter.avatar.startsWith('data:image')) {
      console.error("Attempted to save character with avatar as data URI:", newCharacter.avatar.substring(0,100) + "...");
      toast({
        title: "Avatar Not Ready",
        description: "Avatar hasn't been uploaded yet. Please ensure 'Use This Avatar' completed successfully before saving.",
        variant: "destructive",
      });
      return; // Stop the save process
    }
    // --- End Safeguard ---

    // --- Avatar Upload Logic REMOVED from here --- 
    // The avatar URL should already be in newCharacter.avatar from handleUseAvatar
    const finalAvatarUrl = newCharacter.avatar; // Directly use the URL stored in state

    // --- Prepare character data (excluding avatar upload) --- 
    const finalAdditionalFields = {};
    Object.entries(newCharacter.additionalFields).forEach(([key, value]) => {
      const config = additionalFieldConfigs[key];
      if (config?.type === 'multiselect') {
        finalAdditionalFields[key] = ensureArray(value);
      } else {
        finalAdditionalFields[key] = value;
      }
    });

    const characterDataToSave = {
      // Keep existing fields, use finalAvatarUrl
      id: editingCharacter ? editingCharacter.id : uuidv4(), // Use UUID for new IDs too
      name: newCharacter.name,
      age: newCharacter.age,
      gender: newCharacter.gender,
      avatar: finalAvatarUrl,
      traits: ensureArray(newCharacter.traits),
      specialAbility: ensureArray(newCharacter.specialAbility),
      likes: ensureArray(newCharacter.likes),
      dislikes: ensureArray(newCharacter.dislikes),
      fears: ensureArray(newCharacter.fears),
      dreams: ensureArray(newCharacter.dreams),
      additionalFields: finalAdditionalFields,
      relationships: newCharacter.relationships?.filter(rel => rel.characterId && rel.type) || [],
      isAnimal: newCharacter.isAnimal || false,
      animalType: newCharacter.isAnimal ? newCharacter.animalType : '',
      seed: newCharacter.seed // Persist the seed used for generation
    };

    try {
      if (editingCharacter) {
        await updateCharacter(editingCharacter.id, characterDataToSave);
      } else {
        // Ensure new characters also get relationships updated if needed
        await addCharacter(characterDataToSave);
      }

      // --- Update relationships for OTHER characters --- 
      // (This logic might need refinement depending on how relationships are managed globally)
      const relatedUpdates = [];
      characterDataToSave.relationships.forEach(rel => {
          const oppositeChar = characters.find(char => char.id === rel.characterId);
          if (oppositeChar) {
              const oppositeType = getOppositeRelationship(rel.type, characterDataToSave, oppositeChar); // Pass full objects
              const existingRelIndex = (oppositeChar.relationships || []).findIndex(r => r.characterId === characterDataToSave.id);
              let updatedRelationships = [...(oppositeChar.relationships || [])];
              if (existingRelIndex > -1) {
                 // Update existing relationship if type changed
                 if (updatedRelationships[existingRelIndex].type !== oppositeType) {
                    updatedRelationships[existingRelIndex] = { ...updatedRelationships[existingRelIndex], type: oppositeType };
                 } else {
                    // No change needed for this specific relation
                    return; 
                 }
              } else {
                 // Add new relationship
                 updatedRelationships.push({ characterId: characterDataToSave.id, type: oppositeType });
              }
             
              relatedUpdates.push(updateCharacter(oppositeChar.id, { ...oppositeChar, relationships: updatedRelationships }));
          }
      });
      // Remove relationships from characters no longer related
      characters.forEach(char => {
          if (char.id !== characterDataToSave.id) { // Don't modify the character being saved
              const currentRelationshipIndex = (char.relationships || []).findIndex(rel => rel.characterId === characterDataToSave.id);
              const shouldHaveRelationship = characterDataToSave.relationships.some(rel => rel.characterId === char.id);
              
              if (currentRelationshipIndex > -1 && !shouldHaveRelationship) {
                 // Remove the relationship if it exists but shouldn't anymore
                 const updatedRelationships = (char.relationships || []).filter(rel => rel.characterId !== characterDataToSave.id);
                 relatedUpdates.push(updateCharacter(char.id, { ...char, relationships: updatedRelationships }));
              }
          }
      });

      // Wait for all related updates to complete (optional, depends on state management)
      await Promise.all(relatedUpdates);
      // --- End relationship update --- 

      setIsDialogOpen(false);
      setEditingCharacter(null);
      resetNewCharacter();

    } catch (saveError) {
       console.error("Error saving character:", saveError);
       toast({
         title: "Error Saving Character",
         description: `Failed to save character: ${saveError.message}`,
         variant: "destructive",
       });
    }
  };

  const getCharacterGender = (characterId) => {
    const character = characters.find(char => char.id === characterId);
    return character?.gender || 'Unknown';
  };

  const getOppositeRelationship = (relationType, characterId, relatedCharacterId) => {
    const characterGender = getCharacterGender(characterId);
    const relatedCharacterGender = getCharacterGender(relatedCharacterId);
    
    switch (relationType) {
      // Family relationships
      case 'Father':
        return relatedCharacterGender === 'Female' ? 'Daughter' : 'Son';
      case 'Mother':
        return relatedCharacterGender === 'Female' ? 'Daughter' : 'Son';
      case 'Son':
        return characterGender === 'Female' ? 'Mother' : 'Father';
      case 'Daughter':
        return characterGender === 'Female' ? 'Mother' : 'Father';
      case 'Brother':
        return relatedCharacterGender === 'Female' ? 'Sister' : 'Brother';
      case 'Sister':
        return relatedCharacterGender === 'Female' ? 'Sister' : 'Brother';
      case 'Husband':
        return 'Wife';
      case 'Wife':
        return 'Husband';
      case 'Partner':
        return 'Partner';
      case 'Grandparent':
        return characterGender === 'Female' ? 'Grandmother' : 'Grandfather';
      case 'Grandchild':
        return relatedCharacterGender === 'Female' ? 'Granddaughter' : 'Grandson';
      case 'Grandmother':
        return relatedCharacterGender === 'Female' ? 'Granddaughter' : 'Grandson';
      case 'Grandfather':
        return relatedCharacterGender === 'Female' ? 'Granddaughter' : 'Grandson';
      case 'Aunt':
        return relatedCharacterGender === 'Female' ? 'Niece' : 'Nephew';
      case 'Uncle':
        return relatedCharacterGender === 'Female' ? 'Niece' : 'Nephew';
      case 'Niece':
        return characterGender === 'Female' ? 'Aunt' : 'Uncle';
      case 'Nephew':
        return characterGender === 'Female' ? 'Aunt' : 'Uncle';
      case 'Cousin':
        return 'Cousin';
      case 'Pet':
        return 'Owner';

      // Friendship relationships
      case 'Best Friend':
      case 'Close Friend':
      case 'Friend':
      case 'Acquaintance':
        return relationType;

      // Romantic relationships
      case 'Love Interest':
        return 'Love Interest';
      case 'Ex-partner':
        return 'Ex-partner';

      // Mentorship relationships
      case 'Mentor':
        return 'Mentee';
      case 'Mentee':
        return 'Mentor';
      case 'Teacher':
        return 'Student';
      case 'Student':
        return 'Teacher';
      case 'Guardian':
        return characterGender === 'Female' ? 'Mother' : 'Father';

      // Companionship/Team relationships
      case 'Sidekick':
        return 'Ally';
      case 'Ally':
        return 'Ally';
      case 'Teammate':
        return 'Teammate';
      case 'Colleague':
        return 'Colleague';
      case 'Neighbor':
        return 'Neighbor';

      // Conflict relationships
      case 'Rival':
        return 'Rival';
      case 'Enemy':
        return 'Enemy';
      case 'Nemesis':
        return 'Nemesis';
      case 'Bully':
        return 'Victim';
      case 'Victim':
        return 'Bully';

      default:
        return relationType;
    }
  };

  const resetNewCharacter = () => {
    setNewCharacter({
      id: '',
      name: '',
      age: '',
      gender: 'Female',
      avatar: null,
      traits: [],
      specialAbility: [],
      likes: [],
      dislikes: [],
      fears: [],
      dreams: [],
      additionalFields: {},
      relationships: [],
      seed: null,
      isAnimal: false,
      animalType: ''
    });
    setAvatarPrompt('');
    setGeneratedAvatar(null);
    setIsGenerating(false);
  }

  const handleEditCharacter = (character) => {
    setEditingCharacter(character);
    setAvatarPrompt('');
    setGeneratedAvatar(null);
    setIsGenerating(false);

    // Process additional fields for form display
    const additionalFieldsForForm = {};
    Object.entries(character.additionalFields || {}).forEach(([key, value]) => {
        const config = additionalFieldConfigs[key];
        if (config?.type === 'multiselect') {
            additionalFieldsForForm[key] = ensureArray(value);
        } else {
            additionalFieldsForForm[key] = value || ''; // Default to empty string for text
        }
    });
    
    // Handle cases where core fields might have been stored in additionalFields previously (optional cleanup)
    // delete additionalFieldsForForm.Traits; // Example

    const charDataForForm = {
      ...character,
      avatar: character.avatar && character.avatar.startsWith('http') ? character.avatar : null,
      traits: ensureArray(character.traits),
      specialAbility: ensureArray(character.specialAbility),
      likes: ensureArray(character.likes),
      dislikes: ensureArray(character.dislikes),
      additionalFields: additionalFieldsForForm,
      relationships: character.relationships || [],
      seed: character.seed || null,
      isAnimal: character.isAnimal || false,
      animalType: character.isAnimal ? character.animalType : '',
    };
    setNewCharacter(charDataForForm);
    setIsDialogOpen(true);
  }

  const handleDeleteCharacter = (id) => {
    characters.forEach(char => {
      if (char.id !== id && char.relationships?.some(rel => rel.characterId === id)) {
        const updatedChar = {
          ...char,
          relationships: char.relationships.filter(rel => rel.characterId !== id)
        };
        updateCharacter(char.id, updatedChar);
      }
    });
    deleteCharacter(id);
  }

  const handleAddOptionalField = (fieldName) => {
      const config = additionalFieldConfigs[fieldName];
      const initialValue = config?.type === 'multiselect' ? [] : '';
        setNewCharacter(prev => ({
          ...prev,
          additionalFields: { ...prev.additionalFields, [fieldName]: initialValue }
        }));
      }

  const handleRemoveOptionalField = (fieldName) => {
    setNewCharacter(prev => {
          const updatedFields = { ...prev.additionalFields };
          delete updatedFields[fieldName];
          return { ...prev, additionalFields: updatedFields };
      });
  }

  const availableOptionalFields = baseOptionalFieldNames.filter(
      fieldName => !newCharacter.additionalFields?.hasOwnProperty(fieldName)
  );

  const handleAddRelationship = () => {
    setNewCharacter(prev => ({
      ...prev,
      relationships: [...(prev.relationships || []), { characterId: '', type: '' }]
    }));
  }

  const handleRemoveRelationship = (index) => {
    setNewCharacter(prev => ({
      ...prev,
      relationships: (prev.relationships || []).filter((_, i) => i !== index)
    }));
  }

  const handleRelationshipChange = (index, field, value) => {
    setNewCharacter(prev => {
      const updatedRelationships = [...(prev.relationships || [])];
      updatedRelationships[index] = { ...updatedRelationships[index], [field]: value };
      return { ...prev, relationships: updatedRelationships };
    });
  }

  const availableRelationshipCharacters = characters.filter(char => char.id !== newCharacter.id);

  const toggleGroupExpansion = (groupLabel, e) => {
    e.preventDefault();
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupLabel)) {
        newSet.delete(groupLabel);
      } else {
        newSet.add(groupLabel);
      }
      return newSet;
    });
  }

  const filteredRelationshipTypes = relationshipTypes
    .map(group => ({
      ...group,
      options: group.options.filter(option =>
        option.toLowerCase().includes(relationshipSearchTerm.toLowerCase())
      )
    }))
    .filter(group =>
      group.options.length > 0 ||
      group.label.toLowerCase().includes(relationshipSearchTerm.toLowerCase())
    );

  // Helper - Detect and flatten ['["a","b"]'] pattern
  const ensureArray = (value) => {
    // Helper to clean individual strings (remove surrounding quotes and unnecessary backslashes)
    const cleanString = (str) => {
      if (typeof str !== 'string') return String(str); // Ensure it's a string first
      let cleaned = str.trim();
      // Remove surrounding quotes (double or single)
      if ((cleaned.startsWith('"') && cleaned.endsWith('"')) || (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
        cleaned = cleaned.substring(1, cleaned.length - 1);
      }
      // Replace escaped quotes and backslashes (handle multiple levels)
      try {
        // Attempt to parse as JSON string to unescape correctly
        // This handles cases like '\\"Text\\"' -> 'Text'
        cleaned = JSON.parse(`"${cleaned.replace(/\\"/g, '"').replace(/\\\\/g, '\\')}"`);
      } catch (e) {
        // If JSON.parse fails, fall back to simple replacement (less robust but safe)
        cleaned = cleaned.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
      }
      // Final trim after cleaning
      return cleaned.trim();
    };

    let potentialArray = [];

    if (Array.isArray(value)) {
      // Check for the specific double-wrapped pattern
      if (value.length === 1 && typeof value[0] === 'string') {
        const innerString = value[0].trim();
        if (innerString.startsWith('[') && innerString.endsWith(']')) {
          try {
            potentialArray = JSON.parse(innerString);
          } catch (e) {
            console.warn('[Characters.js] Failed to parse inner string as JSON array:', innerString, e);
            potentialArray = []; // Return empty if inner parse fails
          }
        } else {
          // If it's a single string element not looking like an array, treat it as a single-item array
          potentialArray = [innerString]; // Process this single string item
        }
      } else {
        // Otherwise, treat as a normal array
        potentialArray = value;
      }
    } else if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
        // String looks like JSON array
        try {
          potentialArray = JSON.parse(trimmedValue);
        } catch (e) {
          console.warn('[Characters.js] Failed to parse string as JSON array:', value, e);
          potentialArray = [];
        }
      } else if (trimmedValue) {
         // If it's a non-empty string that doesn't look like an array, treat it as a single element.
         // This case might be debatable depending on expected input, but handles simple string inputs.
         // console.warn('[Characters.js] Received non-JSON string where array expected, treating as single element:', value);
         potentialArray = [trimmedValue];
      } else {
         // Empty string or null/undefined input
         potentialArray = [];
      }
    } else if (value) {
       // Handle potential non-string/non-array inputs (e.g., numbers) by converting to string
       potentialArray = [String(value)];
    }


    // Ensure the result is an array and clean each item
    if (Array.isArray(potentialArray)) {
      return potentialArray.map(item => cleanString(item)).filter(item => item !== ''); // Filter out empty strings after cleaning
    } else {
       // If parsing somehow resulted in non-array (shouldn't happen with current logic, but safety check)
      return [];
    }
  };

  // --- Avatar Handlers ---

  const handleAvatarOptionChange = (value) => {
    setAvatarPrompt(value);
    setGeneratedAvatar(null);
  };

  const handlePromptChange = (event) => {
    setAvatarPrompt(event.target.value);
  };

  const handleGenerateAvatar = useCallback(async () => {
    if (!avatarPrompt || isGenerating) return;

    setIsGenerating(true);
    setGenerationError(null);
    setGeneratedAvatar(null);

    try {
      const authToken = await getToken();
      const response = await fetch('/api/generate-avatar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        },
        body: JSON.stringify({ prompt: avatarPrompt }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || `Generation failed (${response.status})`);
      }

      if (data.image_url) {
        setGeneratedAvatar(data.image_url);
        setGenerationError(null);
      } else {
        throw new Error('No image returned from server');
      }
    } catch (error) {
      console.error('Avatar generation error:', error);
      setGenerationError(error.message);
      setGeneratedAvatar('error');
    } finally {
      setIsGenerating(false);
    }
  }, [avatarPrompt, isGenerating, getToken]);


  const handleUseAvatar = async () => {
    if (!generatedAvatar || generatedAvatar === 'error') {
        toast({
          title: "No Avatar Available",
          description: "No valid generated avatar to use.",
          variant: "destructive",
        });
        return;
    }
    const avatarBlob = dataURItoBlob(generatedAvatar);

    if (!avatarBlob) {
        console.error("Failed to convert base64 to Blob in handleUseAvatar.");
        toast({
          title: "Processing Error",
          description: "Failed to process the generated image. Please try generating again.",
          variant: "destructive",
        });
        return;
    }

    // Show loading/uploading state maybe?
    // setIsUploading(true); // Need to add this state if desired

    try {
        const fileExt = avatarBlob.type.split('/')[1] || 'png';
        const fileName = `avatars/${clerkUser?.id}/${uuidv4()}.${fileExt}`;

        // Upload to Firebase Storage
        const storageRef = ref(storage, fileName);
        const snapshot = await uploadBytes(storageRef, avatarBlob, {
          contentType: avatarBlob.type,
          cacheControl: 'public, max-age=3600',
        });

        // Get the download URL
        const publicUrl = await getDownloadURL(snapshot.ref);

        if (publicUrl) {
          // Update the character state with the URL
          setNewCharacter(prev => ({ ...prev, avatar: publicUrl }));
          // Clear the temporary states
          setGeneratedAvatar(null);
          setAvatarPrompt("");
          setAvatarConfirmed(true); // Mark as confirmed/uploaded
        } else {
          console.error("Upload succeeded, but failed to get public URL.");
          throw new Error("Upload worked, but couldn't retrieve the image URL.");
        }
    } catch (error) {
        console.error("Error in handleUseAvatar during upload/URL retrieval:", error);
        toast({
          title: "Avatar Upload Failed",
          description: `Failed to finalize avatar: ${error.message}. Please try generating/using again.`,
          variant: "destructive",
        });
    } finally {
        // setIsUploading(false); // Turn off loading indicator
    }
  };

  const handleDialogOpenChange = (open) => {
    if (!open) {
      // If closing, reset state and call the setter prop
      setEditingCharacter(null);
      resetNewCharacter();
      setAvatarConfirmed(false);
      setIsDialogOpen(false); // Use prop setter
    } else {
      // If opening (rarely controlled directly by onOpenChange, usually by trigger)
      setIsDialogOpen(true); // Use prop setter
    }
  };

  // --- Correctly define DialogFormContent to include ALL fields --- 
  const DialogFormContent = (
    <div className="grid gap-4 py-4"> {/* Main container for the form grid */}
        {/* --- AVATAR SECTION (Admin Only) --- */}
        {isAdmin && (
          <div className="space-y-3 border-b border-indigo-100 pb-4 col-span-full"> {/* Ensure Avatar section spans full width if needed */}
              <Label className="text-indigo-700 font-semibold text-lg">Avatar Image</Label>
              <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-purple-500 shadow-lg bg-gray-200 flex items-center justify-center relative">
                  {/* Avatar Display Logic */}
                  {isGenerating && !generatedAvatar ? (
                      <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress size={30} sx={{color: 'primary.main'}}/>
                          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'primary.main' }}>
                              Generating...
                          </Typography>
                      </Box>
                  ) : generatedAvatar && generatedAvatar !== 'error' ? (
                      <img
                          src={generatedAvatar}
                          alt="Generated Avatar"
                          className="w-full h-full object-cover"
                      />
                  ) : newCharacter.avatar ? (
                      <img
                          src={newCharacter.avatar.startsWith('data:image') ? newCharacter.avatar : (newCharacter.avatar.startsWith('http') ? newCharacter.avatar : `data:image/png;base64,${newCharacter.avatar}`)}
                          alt={newCharacter.name || 'Character Avatar'}
                          className="w-full h-full object-cover"
                      />
                  ) : (
                      <User className="w-16 h-16 text-gray-400" />
                  )}
              </div>
              <div className="space-y-2">
                  <Label htmlFor="avatar-prompt">Generation Prompt</Label>
                  <Input id="avatar-prompt" placeholder="e.g., Happy child with red hair" value={avatarPrompt} onChange={handlePromptChange} disabled={isGenerating}/>
              </div>
              {generationError && (
                  <Alert variant="destructive" className="mt-2">
                      <LucideAlertCircle className="h-4 w-4" />
                      <AlertTitle>Generation Error</AlertTitle>
                      <AlertDescription>{generationError}</AlertDescription>
                  </Alert>
              )}
              <Button
                  type="button"
                  onClick={handleGenerateAvatar}
                  disabled={isGenerating || !avatarPrompt}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white mt-2"
              >
                  {isGenerating ? <CircularProgress size={16} sx={{color: 'white', mr: 1}}/> : <Sparkles className="mr-2 h-4 w-4" />}
                  {generatedAvatar && generatedAvatar !== 'error' ? 'Regenerate Avatar' : 'Generate Avatar'}
              </Button>
               {generatedAvatar && generatedAvatar !== 'error' && (
                  <Button onClick={handleUseAvatar} size="sm" variant="outline" className="w-full bg-green-500 hover:bg-green-600 text-white mt-2" disabled={isGenerating}>
                      <ImageIcon className="mr-2 h-4 w-4" /> Use This Avatar
                  </Button>
              )}
          </div>
        )}

        {/* --- BASIC INFO FIELDS --- */}
        <div>
            <Label htmlFor="name" className="text-indigo-700">Name *</Label>
            <Input
              id="name"
              value={newCharacter.name}
              onChange={(e) => setNewCharacter(prev => ({ ...prev, name: e.target.value }))}
              className={cn(
                "mt-1 border-2 border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                "placeholder:text-indigo-300"
              )}
              required
            />
        </div>
        <div>
            <Label htmlFor="age" className="text-indigo-700">Age *</Label>
            <Input
              id="age"
              type="number"
              value={newCharacter.age}
              onChange={(e) => setNewCharacter(prev => ({ ...prev, age: parseInt(e.target.value) || '' }))}
              className={cn(
                "mt-1 border-2 border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                "placeholder:text-indigo-300"
              )}
              required
            />
        </div>
        <div>
            <Label htmlFor="gender" className="text-indigo-700">Gender *</Label>
            <Select
              value={newCharacter.gender}
              onValueChange={(value) => setNewCharacter(prev => ({ ...prev, gender: value }))}
              required
            >
              <SelectTrigger className={cn(
                "mt-1 border-2 border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                "bg-white"
              )}>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent className="bg-white border-2 border-indigo-300 shadow-lg">
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Male">Male</SelectItem>
              </SelectContent>
            </Select>
        </div>
        <div className="flex items-center mt-2 col-span-full">
          <input
            type="checkbox"
            id="is-animal"
            checked={newCharacter.isAnimal || false}
            onChange={e => setNewCharacter(prev => ({ ...prev, isAnimal: e.target.checked, animalType: e.target.checked ? prev.animalType : '' }))}
            className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <Label htmlFor="is-animal" className="text-indigo-700">Is Animal?</Label>
        </div>
        {newCharacter.isAnimal && (
          <div className="mt-2 col-span-full">
            <Label htmlFor="animal-type" className="text-indigo-700">Animal Type</Label>
            <Select
              value={newCharacter.animalType || ''}
              onValueChange={value => setNewCharacter(prev => ({ ...prev, animalType: value }))}
            >
              <SelectTrigger className={cn(
                "mt-1 border-2 border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                "bg-white"
              )}>
                <SelectValue placeholder="Select animal type" />
              </SelectTrigger>
              <SelectContent className="bg-white border-2 border-indigo-300 shadow-lg">
                {animalOptions.map(animal => (
                  <SelectItem key={animal} value={animal}>{animal}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* --- TRAITS, ABILITIES, LIKES, DISLIKES --- */}
        <div className="col-span-full">
            <Label htmlFor="traits" className="text-indigo-700">Traits</Label>
            <MultiSelectCreatable
              id="traits"
              optionGroups={traitCategories}
              selectedOptions={ensureArray(newCharacter.traits)}
              onChange={(selected) => setNewCharacter({ ...newCharacter, traits: selected })}
              placeholder="Select or add traits..."
              creatablePlaceholder="Add new trait..."
              label="Character Traits"
            />
        </div>
        <div className="col-span-full">
            <Label htmlFor="specialAbility" className="text-indigo-700">Special Ability</Label>
            <MultiSelectCreatable
              id="specialAbility"
              optionGroups={abilityCategories}
              selectedOptions={ensureArray(newCharacter.specialAbility)}
              onChange={(selected) => setNewCharacter({ ...newCharacter, specialAbility: selected })}
              placeholder="Select or add abilities..."
              creatablePlaceholder="Add new ability..."
              label="Special Abilities"
            />
        </div>
         <div className="col-span-full">
            <Label htmlFor="likes" className="text-indigo-700">Likes</Label>
            <MultiSelectCreatable
              id="likes"
              optionGroups={likeCategories}
              selectedOptions={ensureArray(newCharacter.likes)}
              onChange={(selected) => setNewCharacter({ ...newCharacter, likes: selected })}
              placeholder="Select or add likes..."
              creatablePlaceholder="Add new like..."
              label="Character Likes"
            />
        </div>
        <div className="col-span-full">
            <Label htmlFor="dislikes" className="text-indigo-700">Dislikes</Label>
            <MultiSelectCreatable
              id="dislikes"
              optionGroups={dislikeCategories}
              selectedOptions={ensureArray(newCharacter.dislikes)}
              onChange={(selected) => setNewCharacter({ ...newCharacter, dislikes: selected })}
              placeholder="Select or add dislikes..."
              creatablePlaceholder="Add new dislike..."
              label="Character Dislikes"
            />
        </div>
        
        {/* --- ADDITIONAL FIELDS SECTION --- */}
        <div className="space-y-3 border-t border-b border-indigo-100 py-4 col-span-full">
            <Label className="text-indigo-700 block mb-2 font-semibold">Additional Details:</Label>
            {Object.entries(newCharacter.additionalFields || {}).map(([field, value]) => {
              const config = additionalFieldConfigs[field];
              if (!config) return null;
              return (
                <div key={field} className="flex items-start space-x-2 mt-3">
                  <div className="flex-grow space-y-1">
                    <Label htmlFor={field} className="text-indigo-700 capitalize">{config.label || field}</Label>
                    {config.type === 'multiselect' ? (
                      <MultiSelectCreatable
                        id={field}
                        optionGroups={config.categories}
                        selectedOptions={ensureArray(value)}
                        onChange={(selected) => setNewCharacter(prev => ({
                          ...prev,
                          additionalFields: { ...prev.additionalFields, [field]: selected }
                        }))}
                        placeholder={config.placeholder}
                        creatablePlaceholder={config.creatablePlaceholder}
                        label={`Character ${config.label || field}`}
                      />
                    ) : (
                    <Input
                      id={field}
                      value={value || ''}
                      onChange={(e) => setNewCharacter(prev => ({
                          ...prev,
                          additionalFields: { ...prev.additionalFields, [field]: e.target.value }
                      }))}
                      placeholder={config.placeholder}
                      className={cn(
                          "border-2 border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                          "placeholder:text-indigo-300 w-full"
                      )}
                    />
                    )}
                  </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleRemoveOptionalField(field)}
                      className="bg-red-100 text-red-500 hover:bg-red-200 hover:text-red-800 flex-shrink-0 mt-6"
                      aria-label={`Remove ${config.label || field} field`}
                    >
                      <MinusCircle className="w-4 h-4" />
                    </Button>
                </div>
              );
            })}
            {availableOptionalFields.length > 0 && (
              <div className="pt-4 mt-4 border-t border-indigo-100">
                <Label className="text-sm font-medium text-gray-500 mb-2 block">Add More Details:</Label>
                <div className="flex flex-wrap gap-2">
                  {availableOptionalFields.map((fieldName) => {
                      const config = additionalFieldConfigs[fieldName];
                      return (
                          <Button
                            key={fieldName}
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddOptionalField(fieldName)}
                            className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                          >
                            <Plus className="w-4 h-4 mr-1" /> {config?.label || fieldName}
                          </Button>
                      );
                  })}
                </div>
              </div>
            )}
        </div>

        {/* --- RELATIONSHIPS SECTION --- */}
        <div className="col-span-full">
            <Label className="text-indigo-700">Relationships:</Label>
            {(newCharacter.relationships || []).map((rel, index) => {
              const selectedCharacterIdsInOtherRows = (newCharacter.relationships || [])
                  .filter((_, i) => i !== index)
                  .map(r => r.characterId)
                  .filter(Boolean);
              const charactersForThisRow = characters.filter(char => 
                  char.id !== newCharacter.id &&
                  !selectedCharacterIdsInOtherRows.includes(char.id)
              );
              const selectedCharacter = characters.find(char => char.id === rel.characterId);
              const oppositeRelationType = rel.type && rel.characterId && selectedCharacter ?
                getOppositeRelationship(rel.type, newCharacter, selectedCharacter) : null;

              return (
                <div key={index} className="flex flex-col gap-2 mt-2 p-3 border-2 border-indigo-100 rounded-lg">
                  {/* ... Relationship row JSX ... */}
                  <div className="flex items-center gap-2">
                    <Select
                      value={rel.characterId || ''}
                      onValueChange={(value) => handleRelationshipChange(index, 'characterId', value)}
                    >
                      <SelectTrigger className={cn(
                        "flex-grow border-2 border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                      )}>
                        <SelectValue placeholder="Select character" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {charactersForThisRow.length > 0 ? charactersForThisRow.map((char) => (
                          <SelectItem key={char.id} value={char.id} className="flex justify-start items-center px-1 py-1">
                            <div className="flex items-center w-full">
                              <img 
                                src={char.avatar && (char.avatar.startsWith('http') || char.avatar.startsWith('data:image')) ? char.avatar : "/placeholder.svg?height=24&width=24&text=😊"}
                                alt={char.name} 
                                className="w-6 h-6 rounded-full mr-2 object-cover flex-shrink-0" 
                              />
                              <span>{char.name}</span>
                            </div>
                          </SelectItem>
                        )) : <SelectItem value="" disabled>No available characters</SelectItem>}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleRemoveRelationship(index)}
                      className="bg-red-100 text-red-500 hover:bg-red-200 hover:text-red-600 flex-shrink-0"
                      aria-label="Remove relationship"
                    >
                      <MinusCircle className="h-4 w-4" />
                    </Button>
                  </div>
                  {rel.characterId && (
                    <div className="flex flex-col gap-2">
                      <div className="text-sm text-gray-600">
                        Set <span className="font-semibold text-indigo-600">{newCharacter.name || 'this character'}</span>'s relationship to <span className="font-semibold text-indigo-600">{selectedCharacter?.name || 'selected character'}</span>:
                      </div>
                      <Select
                        value={rel.type || ''}
                        onValueChange={(value) => handleRelationshipChange(index, 'type', value)}
                      >
                        <SelectTrigger className={cn(
                          "flex-grow border-2 border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                        )}>
                          <SelectValue placeholder={`Choose relationship to ${selectedCharacter?.name || '...'}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {relationshipTypes.map((group) => {
                            if (!group || !Array.isArray(group.options) || group.options.length === 0) return null;
                            const isExpanded = expandedGroups.has(group.label);
                            return (
                              <SelectGroup key={group.label}>
                                <SelectLabel 
                                  className="px-2 py-1.5 text-sm font-semibold flex items-center justify-between w-full cursor-pointer hover:bg-accent rounded-sm"
                                  onClick={(e) => toggleGroupExpansion(group.label, e)} 
                                >
                                  <span>{group.label}</span>
                                  <ChevronDown className={cn("h-4 w-4 transition-transform", isExpanded ? "rotate-180" : "")}/>
                                </SelectLabel>
                                {isExpanded && group.options.map((type) => (
                                  <SelectItem key={type} value={type} className="text-sm py-1.5 pr-2">
                                    <span className="pl-4">{type}</span>
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {rel.type && selectedCharacter && oppositeRelationType && (
                        <div className="text-sm text-gray-500 italic">
                          This will set {selectedCharacter.name} as <span className="font-semibold text-indigo-600">{oppositeRelationType}</span> of {newCharacter.name || 'this character'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <Button
                variant="outline"
                size="sm"
                onClick={handleAddRelationship}
                className="mt-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700"
                disabled={characters.filter(c => c.id !== newCharacter.id).length <= (newCharacter.relationships || []).length}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Relationship
              </Button>
        </div>
    </div> // End of main form grid div
  );

  return (
    <>
       {/* Character list display */}
       {/* ... JSX for character cards ... */}
       <div className="px-4 flex flex-wrap justify-center gap-6 mt-6">
         {Array.isArray(characters) && characters.map(character => (
           <Card key={character.id} className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2 rounded-xl border-4 border-indigo-500 flex flex-col w-full max-w-sm">
             {/* ... Card Content ... */} 
            <CardContent className="pt-6 px-4 pb-4 relative flex-grow">
               {/* ... Avatar and Title ... */}
               <div className="absolute top-2 right-2 animate-pulse">
                 <Sparkles className="text-yellow-500" />
               </div>
               <div className="relative mb-4">
                 <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-purple-500 shadow-lg transform hover:scale-105 transition-transform duration-300 bg-gray-200 flex items-center justify-center">
                   {character.avatar && (character.avatar.startsWith('http') || character.avatar.startsWith('/') || character.avatar.startsWith('data:image')) ? (
                       <img 
                         src={character.avatar} 
                         alt={`${character.name}'s avatar`} 
                         className="w-full h-full object-cover" 
                         onError={(e) => { e.target.onerror = null; e.target.src='/placeholder.svg?height=128&width=128&text=🚫'; }}
                       />
                   ) : ( 
                     <ImageIcon size={60} className="text-gray-500" />
                   )} 
                 </div>
                 <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                   {character.age} years
                 </div>
               </div>
               <h2 className="text-2xl font-bold text-center text-indigo-700 mb-2">{character.name}</h2>
               <p className="text-center text-purple-700 mb-4">{character.gender}{character.isAnimal ? ` (${character.animalType || 'Animal'})` : ''}</p>
               {/* Details Accordion */} 
               <Accordion type="single" collapsible className="w-full">
                 <AccordionItem value="details">
                   <AccordionTrigger className="p-2 bg-indigo-50 hover:bg-indigo-100 rounded border border-indigo-200 w-full flex justify-between items-center text-sm text-indigo-600 hover:text-indigo-800">
                     Character Details
                   </AccordionTrigger>
                   <AccordionContent>
                     <div className="space-y-2 text-sm p-2 border border-t-0 border-indigo-200 rounded-b">
                       {/* Display logic using ensureArray */} 
                       {character.traits && ensureArray(character.traits).length > 0 && 
                         <p><span className="font-semibold text-indigo-600">Traits:</span> {ensureArray(character.traits).join(', ')}</p>
                       }
                       {character.specialAbility && ensureArray(character.specialAbility).length > 0 && 
                         <p><span className="font-semibold text-indigo-600">Abilities:</span> {ensureArray(character.specialAbility).join(', ')}</p>
                       }
                       {character.likes && ensureArray(character.likes).length > 0 && 
                         <p><span className="font-semibold text-indigo-600">Likes:</span> {ensureArray(character.likes).join(', ')}</p>
                       }
                       {character.dislikes && ensureArray(character.dislikes).length > 0 && 
                         <p><span className="font-semibold text-indigo-600">Dislikes:</span> {ensureArray(character.dislikes).join(', ')}</p>
                       }
                       {character.fears && ensureArray(character.fears).length > 0 && 
                         <p><span className="font-semibold text-indigo-600">Fears:</span> {ensureArray(character.fears).join(', ')}</p>
                       }
                       {character.dreams && ensureArray(character.dreams).length > 0 && 
                         <p><span className="font-semibold text-indigo-600">Dreams:</span> {ensureArray(character.dreams).join(', ')}</p>
                       }
                       {/* Display Additional Fields */}
                       {character.additionalFields && Object.keys(character.additionalFields).length > 0 && 
                         Object.entries(character.additionalFields).map(([key, value]) => {
                           const config = additionalFieldConfigs[key];
                           const displayValueArray = ensureArray(value);
                           if (displayValueArray.length === 0) return null;
                           return (
                               <p key={key}><span className="font-semibold text-indigo-600 capitalize">{config?.label || key}:</span> {displayValueArray.join(', ')}</p>
                           );
                       })} 
                       {/* Relationship Display Logic */} 
                       {character.relationships && character.relationships.length > 0 && (
                         <div>
                           <p className="font-semibold text-indigo-600 mt-2">Relationships:</p>
                           <ul className="list-disc list-inside pl-4">
                             {character.relationships.map((rel, idx) => {
                               const relatedChar = characters.find(c => c.id === rel.characterId);
                               return relatedChar ? (
                                 <li key={`${rel.characterId}-${idx}`}>{rel.type} of {relatedChar.name}</li>
                               ) : null;
                             })}
                           </ul>
                         </div>
                       )}
                     </div>
                   </AccordionContent>
                 </AccordionItem>
               </Accordion>
             </CardContent>
             {/* Edit/Delete Footer */} 
             <CardFooter className="bg-indigo-50 border-t-2 border-indigo-200 p-3"> {/* Adjusted padding */} 
               <div className="w-full flex justify-between items-center">
                 <Button 
                   variant="outline" 
                   size="sm" 
                   onClick={() => handleEditCharacter(character)} 
                   className="flex-1 mr-2 bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200 hover:text-blue-800 transition-colors duration-300"
                 >
                   <EditIcon sx={{ fontSize: 16 }} className="mr-1"/> Edit
                 </Button>
                 <Button
                   variant="destructive"
                   size="sm"
                   onClick={() => setCharacterToDelete(character)}
                   className="flex-1 ml-2 bg-red-100 text-red-700 border-red-300 hover:bg-red-200 hover:text-red-800 transition-colors duration-300"
                 >
                   <DeleteIcon sx={{ fontSize: 16 }} className="mr-1"/> Delete
                 </Button>
               </div>
             </CardFooter>
           </Card>
         ))}
       </div>

       {/* Button and Dialog for adding/editing characters */}
       {/* ... Dialog structure as corrected previously ... */}
       <div className="w-full text-center mt-6">
         <Dialog 
           open={isDialogOpen}
           onOpenChange={handleDialogOpenChange}
         >
           <DialogTrigger asChild>
             {/* ... Button ... */} 
             <Button
               onClick={() => {
                 resetNewCharacter();
                 setEditingCharacter(null);
                 setIsDialogOpen(true);
               }}
               className="bg-indigo-500 hover:bg-indigo-600 text-white transition-colors duration-300 rounded-full px-6 py-3 text-lg font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
             >
               <div className="flex items-center">
                 <Plus className="w-6 h-6 mr-2" />
                 <span>Create New Character</span>
               </div>
             </Button>
           </DialogTrigger>
           <DialogContent className="sm:max-w-[600px] bg-white p-0 flex flex-col">
             <div className="p-6 pb-4 border-b border-indigo-100">
               <DialogHeader>
                 <DialogTitle className="text-2xl font-bold text-indigo-800 mb-1">
                   {editingCharacter ? 'Edit Character' : 'Create New Character'}
                 </DialogTitle>
                 <DialogDescription className="text-indigo-600">
                   {editingCharacter ? 'Update the details for this character.' : 'Fill in the details for your new character. Required fields are marked with *.'}
                 </DialogDescription>
               </DialogHeader>
             </div>
             <div className="p-6 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 160px)' }}>
               {DialogFormContent} {/* <-- This renders the complete form */} 
             </div>
             <div className="p-6 pt-4 border-t border-indigo-100 bg-gray-50">
               <DialogFooter className="sm:justify-between">
                 <Button variant="outline" type="button" onClick={() => handleDialogOpenChange(false)} className="bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300">
                   Cancel
                 </Button>
                 <Button onClick={handleAddOrUpdateCharacter} disabled={isGenerating} className="bg-indigo-600 text-white hover:bg-indigo-700">
                   {isGenerating ? <CircularProgress size={20} sx={{color: 'white'}}/> : null}
                   {editingCharacter ? 'Update Character' : 'Create Character'}
                 </Button>
               </DialogFooter>
             </div>
           </DialogContent>
         </Dialog>
       </div>

       <AlertDialog
         open={!!characterToDelete}
         onOpenChange={(open) => { if (!open) setCharacterToDelete(null); }}
       >
         <AlertDialogContent>
           <AlertDialogHeader>
             <AlertDialogTitle>Delete this character?</AlertDialogTitle>
             <AlertDialogDescription>
               {characterToDelete ? `${characterToDelete.name} will be permanently removed from your library. This cannot be undone.` : ''}
             </AlertDialogDescription>
           </AlertDialogHeader>
           <AlertDialogFooter>
             <AlertDialogCancel>Cancel</AlertDialogCancel>
             <AlertDialogAction
               onClick={() => {
                 if (characterToDelete) handleDeleteCharacter(characterToDelete.id);
                 setCharacterToDelete(null);
               }}
               className="bg-red-600 text-white hover:bg-red-700"
             >
               Delete
             </AlertDialogAction>
           </AlertDialogFooter>
         </AlertDialogContent>
       </AlertDialog>
     </>
   );
}
